import { Injectable } from '@angular/core';
import { NO_FISCAL_REGISTER_PARAM_CODE, RegisterStatus } from '../@core/constants';
import { Branch } from '../models/branch';
import { BranchRegion } from '../models/branch-region';
import { PhysicalEstablishment } from '../models/physical-establishment';
import { Register } from '../models/register';
import { SalesOrg } from '../models/sales-org';
import { User } from '../models/user';
import { LocalStorageService } from './local-storage.service';
import { ParamsService } from './params.service';
import { RegisterService } from './register.service';
import { SessionStorageService } from './session-storage.service';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {
  constructor(private readonly localStorageService: LocalStorageService, private readonly sessionStorageService: SessionStorageService, private readonly paramsService: ParamsService, private readonly registerService: RegisterService) { }

  readSelectedBranchAndSalesOrg(): { salesOrg: SalesOrg, branch: Branch } {
    let sBSO: any = this.sessionStorageService.get("selectedBranchAndSalesOrg");
    sBSO = sBSO ? JSON.parse(sBSO) as { salesOrg: SalesOrg, branch: Branch } : { salesOrg: new SalesOrg(), branch: new Branch() };

    return sBSO;
  }

  createSelectedBranchAndSalesOrg(selectedBranchAndSalesOrg: { salesOrg: SalesOrg | undefined, branch: Branch }): ConfigurationService {
    this.sessionStorageService.set("selectedBranchAndSalesOrg", JSON.stringify(selectedBranchAndSalesOrg));
    return this;
  }

  readSalesOrg(): Array<SalesOrg> {
    let sO = this.localStorageService.get("salesOrg");
    return sO ? JSON.parse(sO) as Array<SalesOrg> : [];
  }

  createSalesOrg(salesOrg: SalesOrg[]): ConfigurationService {
    this.localStorageService.set("salesOrg", JSON.stringify(salesOrg));
    return this;
  }

  readBranch(): any {
    let br = this.localStorageService.get("branch");
    return br ? JSON.parse(br) : {};

  }

  createBranch(branch: any): ConfigurationService {
    this.localStorageService.set("branch", JSON.stringify(branch));
    return this;
  }

  readPhysicalEstablishment(): PhysicalEstablishment {
    let pE = this.localStorageService.get("physicalEstablishment");
    return pE ? JSON.parse(pE) as PhysicalEstablishment : { address: "", city: "", code: "", country: "", name: "" };
  }

  createPhysicalEstablishment(physicalEstablishment?: PhysicalEstablishment): ConfigurationService {
    this.localStorageService.set("physicalEstablishment", JSON.stringify(physicalEstablishment));
    return this;
  }

  readRegister(): Register {
    let reg = this.localStorageService.get("register");
    return reg ? JSON.parse(reg) as Register : { closeTimeStamp: new Date(0), code: "", currency: "", defaultCash: 0, failedToClose: false, lastPeriod: "", name: "", openTimeStamp: new Date(0), openingTotals: new Map<string, number>(), operator: "", physicalEstablishmentCode: "", status: RegisterStatus.INACTIVE, totals: new Map<string, number>() };
  }
  createRegister(register?: Register): ConfigurationService {
    this.localStorageService.set("register", JSON.stringify(register));
    return this;
  }
  readSupervisor(): User {
    let sup = this.localStorageService.get("supervisor");
    return sup && sup != "undefined" ? (JSON.parse(sup) as User) : new User();
  }
  createSupervisor(user: User | undefined): ConfigurationService {
    this.localStorageService.set("supervisor", JSON.stringify(user))
    return this;
  }

  readRegion(): BranchRegion[] {
    let region = this.localStorageService.get("region");
    if (region != undefined && !Array.isArray(JSON.parse(region))) {
      region = `[${region}]`;
    }

    return region ? JSON.parse(region) as BranchRegion[] : [];
  }

  readRegionCodesList(): string {
    let regions = this.readRegion();
    let regionCodes: string = "";
    regions.forEach(r => {
      regionCodes = `${regionCodes},${r.code}`;
    });

    return regionCodes;
  }

  createRegion(region?: BranchRegion[]): ConfigurationService {
    this.localStorageService.set("region", JSON.stringify(region));
    return this;
  }

  removeConfiguration() {
    this.localStorageService.remove("physicalEstablishment")
      .remove("register")
      .remove("salesOrg")
      .remove("branch")
      .remove("supervisor")
      .remove("selectedBranchAndSalesOrg")
      .remove("region");
  }

  readPreviousConfig() {
    const previousSettings = this.localStorageService.get("settings");

    return previousSettings ? JSON.parse(previousSettings) : {};
  }

  hideMenu() {
    this.sessionStorageService.set("menu", "true");
  }

  showMenu() {
    this.sessionStorageService.remove("menu");
  }

  getMenu(): boolean {
    return (this.sessionStorageService.get("menu") ?? "") == "";
  }

  getCookie(key: string) {
    const nameEQ = key + "=";
    const ca = document.cookie.split(';');
    for (const element of ca) {
      let c = element;
      while (c.startsWith(' ')) { // Remove leading whitespace
        c = c.substring(1, c.length);
      }
      if (c.startsWith(nameEQ)) { // Check if this cookie matches the name
        return decodeURIComponent(c.substring(nameEQ.length, c.length)); // Return decoded value
      }
    }
    return "";
  }

  getCookieObject(key: string) {
    return this.getCookie(key) ? JSON.parse(this.getCookie(key)) : "";
  }

  setCookie(cname: string, cvalue: string, exdays: number) {
    const d = new Date();
    d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
    let expires = "expires=" + d.toUTCString();
    document.cookie = cname + "=" + encodeURIComponent(cvalue) + ";" + expires + ";path=/;SameSite=None;Secure;";
  }

  updateCookies() {
    const physicalEstablishment = this.readPhysicalEstablishment();
    const register = this.readRegister();
    const supervisor = this.readSupervisor();

    if (!physicalEstablishment?.code || !register?.code) {
      return false;
    }

    this.setCookie("physicalEstablishment", JSON.stringify(physicalEstablishment), 365);
    this.setCookie("register", JSON.stringify(register), 365);
    this.setCookie("supervisor", JSON.stringify({ username: supervisor.username, fullname: supervisor.fullname }), 365);
    return true;
  }

  migrateFromPreviousVersion() {
    const previousSettings = this.readPreviousConfig();

    if (!previousSettings.physicalEstablishment || !previousSettings.register || previousSettings.physicalEstablishment.code != "00" || previousSettings.register.code != "00") {
      return false;
    }
    this.createPhysicalEstablishment(previousSettings.physicalEstablishment);
    this.createRegister(previousSettings.register);
    this.createSupervisor(previousSettings.supervisor);
    return true;
  }

  migrateFromCookies() {
    const physicalEstablishment = this.getCookieObject("physicalEstablishment");
    const register = this.getCookieObject("register");
    const supervisor = this.getCookieObject("supervisor");

    if (!physicalEstablishment || !register) {
      return false;
    }
    this.createPhysicalEstablishment(physicalEstablishment);
    this.createRegister(register);
    this.createSupervisor(supervisor);
    return true;
  }


  async configureFromCookies() {
    if (!this.checkAndUpdateCookies() && !this.migrateFromPreviousVersion() && !this.readPhysicalEstablishment()?.code) {
      const params = (await this.paramsService.readParams().toPromise()).response;
      let noFiscalRegister = (await this.registerService.readRegister(params[NO_FISCAL_REGISTER_PARAM_CODE]).toPromise()).response;
      this.createRegister(noFiscalRegister);
      this.createPhysicalEstablishment(new PhysicalEstablishment());
    }
    this.migrateFromCookies();
  }

  checkAndUpdateCookies() {
    const physicalEstablishment = this.getCookie("physicalEstablishment");
    const register = this.getCookie("register");
    const supervisor = this.getCookie("supervisor");

    if (!physicalEstablishment || !register) {
      return false;
    }
    this.setCookie("physicalEstablishment", physicalEstablishment, 365);
    this.setCookie("register", register, 365);
    this.setCookie("supervisor", supervisor, 365);

    return true;
  }

  loadBranchesFromPrevSettings(check: boolean = true) {

    if (this.readSalesOrg()?.length > 0 && check) {
      return false;
    }

    try {
      const previousSettings = this.readPreviousConfig();
      const branch: any = {};
      const salesOrgs = [];
      if (previousSettings.organization1?.code && previousSettings.organization1?.code != "00") {
        branch[previousSettings.organization1?.code] = previousSettings.organization1.branches;
        previousSettings.organization1.branches = undefined;
        salesOrgs.push(previousSettings.organization1);
      }

      if (previousSettings.organization2?.code && previousSettings.organization2?.code != "00") {
        branch[previousSettings.organization2?.code] = previousSettings.organization2.branches;
        previousSettings.organization2.branches = undefined;
        salesOrgs.push(previousSettings.organization2);
      }

      if (previousSettings.organization3?.code && previousSettings.organization2?.code != "00") {
        branch[previousSettings.organization3?.code] = previousSettings.organization3.branches;
        previousSettings.organization3.branches = undefined;
        salesOrgs.push(previousSettings.organization3);
      }
      if (salesOrgs[0] && Object.entries(branch)[0]) {
        this.createSalesOrg(salesOrgs);
        this.createBranch(branch);
        this.createSelectedBranchAndSalesOrg({ salesOrg: salesOrgs[0], branch: branch[salesOrgs[0].code][0] });
      }
      else {
        return false;
      }
    } catch (error) {
      console.log(error);
      return false;
    }

    return true;


  }

  readSigninWithoutCaptcha(): boolean {
    return Boolean(this.localStorageService.get("signinWithoutCaptcha"));
  }

}
